import { useContext, useEffect, useState } from "react";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import {
  Error,
  FormDataType,
} from "@shared/features/bots/BotForm/common/types";
import { useSharedBotNameInput } from "@shared/features/bots/BotForm/common/hooks";
import useAdvanced from "@shared/features/bots/BotForm/useAdvanced";
import useMarginSelectors from "@shared/features/bots/BotForm/useMarginSelectors";
import { useClientsData } from "@shared/utils/hooks/misc";
import { GroupData, OpenMessage } from "@shared/types";
import useAlternativeUsdPairs from "@shared/features/bots/BotForm/General/useAlternativeUsdPairs";
import useFollowFuturesAsSpot from "@shared/features/bots/BotForm/common/hooks/useFollowFuturesAsSpot";

interface useSignalsBotOverviewProps {
  openMessage: OpenMessage;
  selectedAccountId?: number;
  groupData?: GroupData;
}

const useSignalsBotOverview = ({
  openMessage,
  selectedAccountId,
  groupData,
}: useSignalsBotOverviewProps) => {
  const {
    setSubGroup,
    setAccount,
    setSymbols,
    addInitializedData,
    errors,
    isLoading,
    symbolsMetadata,
    amountType,
    entryStrategy,
  } = useContext(BotFormContext)!;
  const [isConfirmCheckboxChecked, setIsConfirmCheckboxChecked] =
    useState(false);
  const [showCheckboxError, setShowCheckboxError] = useState(false);

  useSharedBotNameInput({ initialize: true });
  useAdvanced({ openMessage });
  useMarginSelectors({ openMessage });
  useAlternativeUsdPairs({});
  const futuresAsSpot = useFollowFuturesAsSpot();
  const { getAccountById } = useClientsData();

  const isMyGroup = groupData?.expiration_timestamp !== undefined;
  const selectedAccount = selectedAccountId
    ? getAccountById(selectedAccountId)
    : undefined;

  const botAmountError = errors?.includes(Error.GENERAL_AMOUNT);
  const uncheckedCheckboxError = !isMyGroup && !isConfirmCheckboxChecked;
  const isFormReady = !!amountType && !!entryStrategy;
  const isCreateSignalsBotDisabled =
    botAmountError ||
    uncheckedCheckboxError ||
    !selectedAccount ||
    !isFormReady;

  useEffect(() => {
    if (groupData) {
      setSubGroup?.({
        name: groupData?.name,
        id: groupData?.id,
        image: groupData?.image,
      });
    }
  }, [groupData?.id]);

  useEffect(() => {
    if (selectedAccount && symbolsMetadata) {
      setAccount?.(selectedAccount);
      setSymbols?.(
        symbolsMetadata.getAccountActiveSymbols(selectedAccount?.client_id) ||
          []
      );
      addInitializedData(FormDataType.ACCOUNT_AND_SYMBOL);
    }
  }, [selectedAccount, symbolsMetadata]);

  return {
    futuresAsSpot,
    isCreateSignalsBotDisabled,
    isFormReady,
    selectedAccount,
    isMyGroup,
    isConfirmCheckboxChecked,
    setIsConfirmCheckboxChecked,
    showCheckboxError,
    setShowCheckboxError,
    isLoading,
    uncheckedCheckboxError,
  };
};

export default useSignalsBotOverview;
